import { useMutation } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect, useReducer } from "react";
import * as Yup from "yup";

import { Button, getInput } from "../../../../components/ui";
import {
  EXPORT_SAGE_CSV_ACCOUNTS,
  EXPORT_SAGE_CSV_TRANSACTIONS,
} from "../../../../graphql/mutation/Item";
import { useCompanies } from "../../../../hooks/useCompanies";

const initialExportState = {
  error: null,
  total: null,
  exported: null,
};

const ACTIONS = {
  RESET: "RESET",
  SET_ERROR: "SET_ERROR",
  SET_RESULTS: "SET_RESULTS",
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      return initialExportState;
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };
    case ACTIONS.SET_RESULTS:
      return {
        ...state,
        total: action.payload.total,
        exported: action.payload.exported,
      };
    default:
      return state;
  }
};

const ExportSageAccounts = props => {
  const { closeModal, isOpen } = props;
  const [state, dispatch] = useReducer(reducer, initialExportState);
  const { companies } = useCompanies();
  const [exportSageCsvAccounts, { loading: accountsLoading }] = useMutation(
    EXPORT_SAGE_CSV_ACCOUNTS,
    { refetchQueries: ["FetchItemsV2", "FetchInvoice"] },
  );
  const [exportSageCsvTransactions, { loading: transactionsLoading }] =
    useMutation(EXPORT_SAGE_CSV_TRANSACTIONS, {
      refetchQueries: ["FetchItemsV2", "FetchInvoice"],
    });
  const loading = accountsLoading || transactionsLoading;

  const formik = useFormik({
    initialValues: {
      exportType: "accounts",
    },
    validationSchema: Yup.object({
      companyId: Yup.string().required("Please select a company"),
      toDate: Yup.string().required("Please select a date"),
    }),
    onSubmit: async values => {
      const { exportType, ...rest } = values;
      const mutationFn =
        exportType === "transactions"
          ? exportSageCsvTransactions
          : exportSageCsvAccounts;
      const key =
        exportType === "transactions"
          ? "exportSageCsvTransactions"
          : "exportSageCsvAccounts";

      dispatch({ type: ACTIONS.RESET });

      try {
        const response = await mutationFn({
          variables: { input: { ...rest } },
        });
        const data = response?.data?.[key] || {};
        const { success = false, url, total, exported, error } = data;

        if (success) {
          dispatch({ type: ACTIONS.SET_RESULTS, payload: { total, exported } });
          window.open(url, "_blank");
        } else {
          throw new Error(error || "Failed to export Sage CSV");
        }
      } catch (error) {
        dispatch({ type: ACTIONS.SET_ERROR, payload: error });
      }
    },
  });

  useEffect(() => {
    dispatch({ type: ACTIONS.RESET });
    formik?.validateForm();

    return () => {
      formik.resetForm();
    };
  }, [isOpen]);

  const inputs = [
    {
      label: "Company",
      name: "companyId",
      type: "multi-select",
      options: companies?.edges?.map(item => ({
        label: item.name,
        value: item.id,
      })),
    },
    {
      label: "To-Date",
      name: "toDate",
      type: "date",
    },
    {
      label: "Export Type",
      name: "exportType",
      type: "select",
      options: [
        {
          label: "Accounts",
          value: "accounts",
        },
        {
          label: "Transactions",
          value: "transactions",
        },
      ],
    },
  ];

  return (
    <>
      <div className="w-50 grid-rows grid gap-x-16 gap-y-4 p-6">
        {inputs?.map((item, index) => {
          const inputProps = {
            ...item,
            formik,
          };
          return <div key={index}>{getInput(inputProps)}</div>;
        })}
      </div>
      {state.total && (
        <div className="mt-3 inline-flex items-center justify-center border-transparent px-4 py-2 font-medium sm:text-sm">
          Exported
          <span className="border-transparent px-2 font-bold">
            {state.exported} out of {state.total}
          </span>
          {formik.values.exportType === "transactions"
            ? "transactions"
            : "accounts"}
        </div>
      )}
      {state.error && (
        <div className="mt-3 inline-flex items-center justify-center border-transparent px-4 py-2 font-bold font-medium text-red-800 sm:text-sm">
          {state.error?.message ||
            state.error?.graphQLErrors?.map?.(({ message }) => `${message} `) ||
            "An error occurred"}
        </div>
      )}
      <div className="mt-4">
        <Button
          label={state.total ? "Done" : loading ? "Exporting" : "Export"}
          disabled={!formik.isValid || loading}
          onClick={state.total ? closeModal : formik.submitForm}
          className={`!w-full ${state.total ? "bg-green-600 hover:bg-green-700" : ""}`}
        />
      </div>
    </>
  );
};

export default ExportSageAccounts;
